import { config } from "../config";
import { getAccessToken } from "../auth/cognito";
import type {
  CreateMeetInput,
  CarpoolEntry,
  DiscoveredMeet,
  InvitePreview,
  InviteSummary,
  JoinCode,
  MeetDetail,
  MeetListItem,
  MeetComment,
  Paged,
  Profile,
  RsvpList,
  RsvpStatus,
  SourceHealth,
  SourceHealthSummary,
  TeamMember,
  TeamStandingRow,
  TeamRoster,
  RosterAllowance,
  RosterLevel,
  TeamSummary,
  Wrestler,
  MatchRecord,
  WrestlerStats,
  AdminUser,
  ActivityResponse,
} from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  auth?: boolean; // default true
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, auth = true } = opts;

  const url = new URL(config.apiBaseUrl + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth) {
    const token = await getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const resp = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) return undefined as T;

  const text = await resp.text();
  const json = text ? JSON.parse(text) : undefined;

  if (!resp.ok) {
    const err = json?.error ?? { code: "UNKNOWN", message: resp.statusText };
    throw new ApiError(resp.status, err.code, err.message);
  }
  return json as T;
}

export const api = {
  // Profile
  getMe: () => request<Profile>("/me"),
  updateMe: (input: Partial<Profile> & { homeAddress?: Profile["homeAddress"] }) =>
    request<Profile>("/me", { method: "PUT", body: input }),

  // Teams
  listTeams: (includeArchived?: boolean) =>
    request<Paged<TeamSummary>>("/teams", {
      query: includeArchived ? { includeArchived: "true" } : undefined,
    }),
  createTeam: (name: string) =>
    request<{ teamId: string; name: string }>("/teams", {
      method: "POST",
      body: { name },
    }),
  updateTeam: (teamId: string, input: { name?: string; archived?: boolean }) =>
    request<{ teamId: string; name: string; archived: boolean }>(
      `/teams/${teamId}`,
      { method: "PATCH", body: input },
    ),
  deleteTeam: (teamId: string) =>
    request<void>(`/teams/${teamId}`, { method: "DELETE" }),
  listMembers: (teamId: string) =>
    request<Paged<TeamMember>>(`/teams/${teamId}/members`),
  getTeamStandings: (teamId: string) =>
    request<{ teamId: string; items: TeamStandingRow[] }>(
      `/teams/${teamId}/standings`,
    ),
  getCoachDashboard: (teamId: string) =>
    request<import("./types").CoachDashboard>(`/teams/${teamId}/dashboard`),
  getTeamHype: (teamId: string) =>
    request<{ text: string | null; cached: boolean }>(
      `/hype/team/${teamId}`,
    ),
  getMeetHype: (meetId: string) =>
    request<{ text: string | null; cached: boolean }>(
      `/hype/meet/${meetId}`,
    ),
  getTeamCalendarLink: (teamId: string) =>
    request<{
      token: string | null;
      httpsUrl: string | null;
      webcalUrl: string | null;
    }>(`/teams/${teamId}/calendar-link`),
  getTeamRoster: (teamId: string) =>
    request<TeamRoster>(`/teams/${teamId}/roster`),
  saveRosterConfig: (
    teamId: string,
    input: {
      level: RosterLevel;
      baseWeightClasses?: number[];
      allowance: RosterAllowance;
    },
  ) =>
    request(`/teams/${teamId}/roster/config`, {
      method: "PUT",
      body: input,
    }),
  addRosterEntry: (
    teamId: string,
    input: {
      weightClass: number;
      wrestlerName: string;
      parentName?: string;
      parentEmail?: string;
      parentPhone?: string;
      notes?: string;
      wrestlerId?: string;
    },
  ) =>
    request<{ entryId: string }>(`/teams/${teamId}/roster/entries`, {
      method: "POST",
      body: input,
    }),
  updateRosterEntry: (
    teamId: string,
    entryId: string,
    input: Partial<{
      weightClass: number;
      wrestlerName: string;
      parentName: string;
      parentEmail: string;
      parentPhone: string;
      notes: string;
      wrestlerId: string | null;
    }>,
  ) =>
    request(`/teams/${teamId}/roster/entries/${entryId}`, {
      method: "PUT",
      body: input,
    }),
  deleteRosterEntry: (teamId: string, entryId: string) =>
    request<void>(`/teams/${teamId}/roster/entries/${entryId}`, {
      method: "DELETE",
    }),
  addMember: (teamId: string, email: string, role: "admin" | "parent") =>
    request(`/teams/${teamId}/members`, {
      method: "POST",
      body: { email, role },
    }),
  joinTeam: (code: string) =>
    request<{ teamId: string; role: "parent" }>("/teams/join", {
      method: "POST",
      body: { code },
    }),
  /**
   * Join several teams at once by code (onboarding). Attempts each code and
   * returns a per-code result so partial success is visible to the user.
   */
  joinTeams: async (codes: string[]) => {
    const results: { code: string; ok: boolean; message?: string }[] = [];
    for (const raw of codes) {
      const code = raw.trim();
      if (!code) continue;
      try {
        await request<{ teamId: string }>("/teams/join", {
          method: "POST",
          body: { code },
        });
        results.push({ code, ok: true });
      } catch (e) {
        results.push({ code, ok: false, message: (e as Error).message });
      }
    }
    return results;
  },
  createJoinCode: (teamId: string, expiresInDays?: number) =>
    request<JoinCode>(`/teams/${teamId}/join-codes`, {
      method: "POST",
      body: { expiresInDays },
    }),
  listJoinCodes: (teamId: string) =>
    request<Paged<JoinCode>>(`/teams/${teamId}/join-codes`),
  resolveJoinCode: (code: string) =>
    request<{ teamId: string; name: string; valid: boolean }>(
      `/join-codes/${encodeURIComponent(code)}`,
      { auth: false },
    ),

  // Wrestlers (a parent's kids)
  listWrestlers: () => request<Paged<Wrestler>>("/wrestlers"),
  addWrestler: (input: {
    displayName: string;
    teamId?: string;
    weightClass?: string;
    club?: string;
    floUrl?: string;
    riUrl?: string;
    dob?: string;
  }) => request<Wrestler>("/wrestlers", { method: "POST", body: input }),
  removeWrestler: (wrestlerId: string) =>
    request<void>(`/wrestlers/${wrestlerId}`, { method: "DELETE" }),
  updateWrestler: (
    wrestlerId: string,
    input: Partial<{
      displayName: string;
      weightClass: string;
      club: string;
      teamId: string;
      floUrl: string;
      riUrl: string;
    }>,
  ) =>
    request<Wrestler>(`/wrestlers/${wrestlerId}`, {
      method: "PUT",
      body: input,
    }),
  getWrestlerMatches: (wrestlerId: string) =>
    request<{ items: MatchRecord[]; stats?: WrestlerStats[] }>(
      `/wrestlers/${wrestlerId}/matches`,
    ),
  getWrestlerScouting: (wrestlerId: string) =>
    request<import("./types").ScoutingResponse>(
      `/wrestlers/${wrestlerId}/scouting`,
    ),

  // Meets
  listMeets: (query: {
    scope?: "mine" | "public";
    sort?: "date" | "distance";
    radius?: number;
    from?: string;
    to?: string;
    q?: string;
    teamId?: string;
  }) => request<Paged<MeetListItem>>("/meets", { query }),
  getMeet: (meetId: string) => request<MeetDetail>(`/meets/${meetId}`),
  createMeet: (input: CreateMeetInput) =>
    request<MeetDetail>("/meets", { method: "POST", body: input }),
  updateMeet: (meetId: string, input: Partial<CreateMeetInput>) =>
    request<MeetDetail>(`/meets/${meetId}`, { method: "PUT", body: input }),
  setMeetTeams: (meetId: string, invitedTeamIds: string[]) =>
    request<{ meetId: string; invitedTeamIds: string[] }>(
      `/meets/${meetId}/teams`,
      { method: "PUT", body: { invitedTeamIds } },
    ),
  getMeetLineup: (meetId: string) =>
    request<import("./types").EventLineupView>(`/meets/${meetId}/lineup`),
  saveMeetLineup: (
    meetId: string,
    slots: { weightClass: number; rosterEntryId: string }[],
  ) =>
    request<{ meetId: string; slots: unknown[] }>(`/meets/${meetId}/lineup`, {
      method: "PUT",
      body: { slots },
    }),
  cancelMeet: (meetId: string) =>
    request<{ meetId: string; status: string }>(`/meets/${meetId}/cancel`, {
      method: "POST",
    }),

  // RSVPs
  listRsvps: (meetId: string) => request<RsvpList>(`/meets/${meetId}/rsvps`),
  setRsvp: (meetId: string, status: RsvpStatus, wrestlerIds?: string[]) =>
    request<{
      status: RsvpStatus;
      counts: RsvpList["counts"];
      calendarHtmlLink?: string;
    }>(`/meets/${meetId}/rsvp`, {
      method: "PUT",
      body: { status, wrestlerIds },
    }),
  withdrawRsvp: (meetId: string) =>
    request<void>(`/meets/${meetId}/rsvp`, { method: "DELETE" }),

  // Private per-user meet notes
  getNote: (meetId: string) =>
    request<{ meetId: string; note: string; updatedAt?: string }>(
      `/meets/${meetId}/note`,
    ),
  saveNote: (meetId: string, note: string) =>
    request<{ meetId: string; note: string }>(`/meets/${meetId}/note`, {
      method: "PUT",
      body: { note },
    }),

  // Carpool
  listCarpool: (meetId: string) =>
    request<{ offers: CarpoolEntry[]; requests: CarpoolEntry[] }>(
      `/meets/${meetId}/carpool`,
    ),
  postCarpool: (
    meetId: string,
    body: {
      kind: "offer" | "request";
      seats?: number;
      pickupArea?: string;
      departureTime?: string;
      notes?: string;
    },
  ) => request<CarpoolEntry>(`/meets/${meetId}/carpool`, { method: "POST", body }),
  deleteCarpool: (meetId: string, entryId: string) =>
    request<void>(`/meets/${meetId}/carpool/${entryId}`, { method: "DELETE" }),

  // Shared team meet thread (comments)
  listComments: (meetId: string) =>
    request<{ items: MeetComment[] }>(`/meets/${meetId}/comments`),
  postComment: (meetId: string, body: string) =>
    request<MeetComment>(`/meets/${meetId}/comments`, {
      method: "POST",
      body: { body },
    }),
  deleteComment: (meetId: string, commentId: string, createdAt: string) =>
    request<void>(`/meets/${meetId}/comments/${commentId}`, {
      method: "DELETE",
      query: { createdAt },
    }),

  // Invites
  createInvites: (meetId: string, emails: string[]) =>
    request<{ invites: InviteSummary[] }>(`/meets/${meetId}/invites`, {
      method: "POST",
      body: { emails },
    }),
  listInvites: (meetId: string) =>
    request<{ items: InviteSummary[] }>(`/meets/${meetId}/invites`),
  getMeetInvitable: (meetId: string) =>
    request<{
      items: {
        userId: string;
        displayName: string;
        email?: string;
        role: "coach" | "parent";
        status: "none" | "pending" | "accepted";
      }[];
      candidates: {
        userId: string;
        displayName: string;
        email?: string;
        status: "none" | "pending" | "accepted";
      }[];
    }>(`/meets/${meetId}/invitable`),
  inviteMeetMembers: (meetId: string, memberUserIds: string[]) =>
    request<{ invites: InviteSummary[] }>(`/meets/${meetId}/invites`, {
      method: "POST",
      body: { memberUserIds },
    }),
  inviteCandidate: (meetId: string, userId: string) =>
    request<{ addedToTeam: boolean; invited: boolean }>(
      `/meets/${meetId}/invite-candidate`,
      { method: "POST", body: { userId } },
    ),
  resolveInvite: (token: string) =>
    request<InvitePreview>(`/invites/${encodeURIComponent(token)}`, {
      auth: false,
    }),
  acceptInvite: (token: string, rsvp?: RsvpStatus) =>
    request<{ meetId: string; status: string; myRsvp?: RsvpStatus }>(
      `/invites/${encodeURIComponent(token)}/accept`,
      { method: "POST", body: { rsvp } },
    ),

  // Calendar
  googleAuthUrl: () =>
    request<{ authUrl: string }>("/calendar/google/auth-url"),
  unlinkGoogle: () =>
    request<void>("/calendar/google/link", { method: "DELETE" }),
  addToCalendar: (meetId: string, action: "create" | "resync") =>
    request<{ googleEventId: string; htmlLink?: string }>(
      `/meets/${meetId}/calendar`,
      { method: "POST", body: { action } },
    ),
  icsUrl: (meetId: string) => `${config.apiBaseUrl}/meets/${meetId}/calendar.ics`,

  // Discovery (public browse + admin review + add-to-team)
  listDiscovered: (query: {
    state?: string;
    from?: string;
    to?: string;
    q?: string;
    sort?: "date" | "state";
    limit?: number;
    minDifficulty?: number;
    maxDifficulty?: number;
    format?: string;
    level?: string;
  }) =>
    request<{ items: DiscoveredMeet[]; count: number }>("/discovery/meets", {
      query,
      auth: false,
    }),
  getDiscovered: (id: string) =>
    request<DiscoveredMeet>(`/discovery/meets/${id}`, { auth: false }),
  reviewQueue: (state?: string) =>
    request<{ items: DiscoveredMeet[]; count: number }>("/discovery/review", {
      query: { state },
    }),
  listSources: () =>
    request<{ items: SourceHealth[]; summary: SourceHealthSummary }>(
      "/discovery/sources",
    ),
  addManualMeet: (input: {
    title: string;
    startDate: string;
    state: string;
    sourceUrl: string;
    startTime?: string;
    venueName?: string;
    city?: string;
    zip?: string;
    address?: string;
    format?: string;
    sessionDetails?: string;
    registrationInfo?: string;
  }) =>
    request<{ discoveryId: string; status: string; title: string }>(
      "/discovery/manual",
      { method: "POST", body: input },
    ),
  driveTimes: (discoveryIds: string[]) =>
    request<{
      zip: string | null;
      items: Record<
        string,
        {
          distanceText?: string;
          durationText?: string;
          durationSeconds?: number;
          unavailable?: boolean;
        }
      >;
    }>("/discovery/drive-times", {
      method: "POST",
      body: { discoveryIds },
    }),
  approveDiscovered: (id: string) =>
    request<{ discoveryId: string; status: string }>(
      `/discovery/meets/${id}/approve`,
      { method: "POST" },
    ),
  rejectDiscovered: (id: string) =>
    request<{ discoveryId: string; status: string }>(
      `/discovery/meets/${id}/reject`,
      { method: "POST" },
    ),
  addDiscoveredToTeam: (id: string, teamId: string, visibility?: string) =>
    request<{ meetId: string; teamId: string }>(
      `/discovery/meets/${id}/add-to-team`,
      { method: "POST", body: { teamId, visibility } },
    ),

  // Admin (platform-admin only; server enforces)
  adminListUsers: () =>
    request<{ items: AdminUser[]; count: number }>("/admin/users"),
  adminAssignTeam: (
    userId: string,
    teamId: string,
    role: "admin" | "parent",
  ) =>
    request<{ userId: string; teamId: string; role: string }>(
      `/admin/users/${encodeURIComponent(userId)}/teams`,
      { method: "POST", body: { teamId, role } },
    ),
  adminRemoveTeam: (userId: string, teamId: string) =>
    request<void>(
      `/admin/users/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}`,
      { method: "DELETE" },
    ),
  adminActivity: (hours?: number) =>
    request<ActivityResponse>("/admin/activity", {
      query: hours ? { hours } : undefined,
    }),

  // Client usage telemetry (fire-and-forget; never throws into UI).
  trackActivity: (path: string, title?: string) =>
    request<{ recorded: boolean }>("/activity", {
      method: "POST",
      body: { path, title, action: "page_view" },
    }).catch(() => undefined),
};
